/* Copyright (c) 2023-2026, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */

import {
    AfterViewInit,
    ContentChildren,
    Directive,
    ElementRef,
    Input,
    NgZone,
    OnDestroy,
    QueryList
} from '@angular/core';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { MatDialog } from '@angular/material/dialog';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { KeyboardEventArrowKey } from 'utils/lib/web-events/keyboard-event';
import { fromEventElementContainsTarget } from 'crimson/lib/accessibility/from-event-element-contains-target.operator';
import { getSpatialNavigationDirection } from '../../services/spatial-navigation/spatial-navigation';
import { SpatialNavigationService } from '../../services/spatial-navigation/spatial-navigation.service';
import { ContainFocusArgs } from '../../services/spatial-navigation/spatial-navigation.types';
import { FastSpatialNavigationItemDirective } from './fast-spatial-navigation-item.directive';

type OneDimensionFastSpatialNavigationOrientation = 'vertical' | 'horizontal';

/**
 * A directive that provides fast navigation between its focusable children based on their document order
 *
 * The directive speeds up the spaitial navigation search if its focusable children have a visual order
 * equal to its document order in the html. The directive is supposed to be used along with the
 * nvFastSpatialNavigationItem.
 * It is also important that the nvFastSpatialNavigationItem should reside in the
 * same template with the nvFast1DSpatialNavigation; otherwise, it won't work.
 *
 * @example
 *
 * The following cases shows several usages of this directives
 *
 * 1. the item itself is a focusable
 *
 * ```html
 * <div class="container" nvFast1DSpatialNavigation orientation="vertical">
 *  <div class="item" tabindex="0" nvFastSpatialNavigationItem>item 1</div>
 *  <div class="item" tabindex="0" nvFastSpatialNavigationItem>item 2</div>
 *  <div class="item" tabindex="0" nvFastSpatialNavigationItem>item 3</div>
 *  <div class="item" tabindex="0" nvFastSpatialNavigationItem>item 4</div>
 * </div>
 * ```
 *
 * in this case, since the div element has a "block" value of its css property "display" as default,
 * we expect items to be stacked vertically, so we set orientation to "vertical".
 * in this way, we can move the focus between items by pressing up and down key.
 *
 * 2. the item is an angular component, and its focusable is inside the template.
 *
 * let's say we have a template named "item" that has a HTML structure like this
 *
 * ```html
 * <div>
 *  <div tabindex="0">I am focusable</div>
 * </div>
 * ```
 *
 * in the container template
 *
 * ```html
 * <div nvFast1DSpatialNavigation orientation="vertical">
 *   <item nvFastSpatialNavigationItem></item>
 *   <item nvFastSpatialNavigationItem></item>
 *   <item nvFastSpatialNavigationItem></item>
 * </div>
 * ```
 *
 * when the nvFast1DSpatialNavigation dispatches focus to a nvFastSpatialNavigationItem,
 * the nvFastSpatialNavigationItem searches for the first focusable inside its element tree. the
 * reason that the focusable is searched during this phase is because a focusable sometimes
 * reside in a different template as the container. if you want to use nvFastSpatialNavigationItem
 * this way, it would be better if there is only one focusable inside the nvFastSpatialNavigationItem;
 * otherwise, it may produce unexpected result.
 *
 * 3. the item is an angular component, and it contains multiple focusable inside the template.
 *
 * assume we have a component named "item-container", it has a structure like the following
 *
 * ```html
 * <div>
 *   <div tabindex="0">focusable 1</div>
 *   <div tabindex="0">focusable 2</div>
 * </div>
 * ```
 *
 * in our container template
 *
 * ```html
 * <div nvFast1DSpatialNavigation orientation="vertical">
 *   <item-container nvFastSpatialNavigationItem></item-container>
 *   <item-container nvFastSpatialNavigationItem></item-container>
 * </div>
 * ```
 *
 * now comes the question that when this item-container is about to receive focus, which element should nvFastSpatialNavigationItem
 * dispatch focus to?
 * by default, nvFastSpatialNavigationItem searches the first focusable inside the component's subtree, and
 * move the focus to it. in this case, focusabe 1 will always receive focus. however, this behavior doesn't look natural.
 * for example, if we move the focus between game rows in the gfn mall, it is strange that the focus is moved to the first
 * focusable each time we switch to a new game row. therefore, we can use nvSpatialNavigationContainer to change the behavior.
 * now our item-container template looks like this
 *
 * ```html
 * <div nvSpatialNavigationContainer [captureFocus]="true" captureAndTransitFocusPolicy="closest">
 *  <div>focusable 1</div>
 *  <div>focusable 2</div>
 * </div>
 * ```
 *
 * our container template still has the same layout.
 *
 * nvSpatialNavigationContainer turns the item-container to a big focusable, so we can find the outer focusable
 * of nvSpatialNavigationContainer first. When we dispatch "navbeforefocus" to the nvSpatialNavigationContainer. it automatically
 * searches for the closest focusable and moves the focus for us.
 *
 */
@Directive({
    selector: '[nvFast1DSpatialNavigation]',
    standalone: true
})
export class FastOneDimensionSpatialNavigationDirective implements AfterViewInit, OnDestroy {
    /**
     * provide manual control over whether to intercept keydown event
     *
     * the reason to provide this input parameter is that we sometimes want to enable/disable this directive because
     * the structure of its child elements changes and doesn't meet the requirement of this directive anymore.
     */
    @Input() get enableFastNavigation() {
        return this._enalbeFastNavigation;
    }
    set enableFastNavigation(value: any) {
        this._enalbeFastNavigation = coerceBooleanProperty(value);
    }
    private _enalbeFastNavigation = true;

    /**
     * the visual order of child elements. it could be either vertical or horizontal
     *
     * 1. vertical: listen to "ArrowUp" and "ArrowDown" keyboard event.
     * 2. horizontal: listen to "ArrowLeft" and "ArrowRight" keyboard event.
     */
    @Input() get orientation() {
        return this._orientation;
    }
    set orientation(value: OneDimensionFastSpatialNavigationOrientation) {
        this._orientation = value;
    }
    private _orientation: OneDimensionFastSpatialNavigationOrientation = 'horizontal';

    /**
     * decide whether to keep focus inside the container if there is no candidate inside the child elements
     *
     * if a boolean value is given, prevent a user from navigating outside the container on all directions.
     * if a array of directions is given, only prevent a user from navigating outside the container for given
     * directions.
     */
    @Input() get containFocus(): ContainFocusArgs {
        return this._containFocus;
    }
    set containFocus(value: any) {
        if (Array.isArray(value)) {
            this._containFocus = value;
        } else {
            this._containFocus = coerceBooleanProperty(value);
        }
    }
    private _containFocus: ContainFocusArgs = false;

    @Input() get isRTL() {
        return this._isRTL;
    }
    set isRTL(value: any) {
        this._isRTL = coerceBooleanProperty(value);
    }
    private _isRTL = false;

    private destroy$ = new Subject<void>();

    constructor(
        private container: ElementRef<HTMLElement>,
        private spatialNavigationService: SpatialNavigationService,
        private ngZone: NgZone,
        private matDialog: MatDialog
    ) {}

    @ContentChildren(FastSpatialNavigationItemDirective, { descendants: true })
    private items: QueryList<FastSpatialNavigationItemDirective>;

    ngAfterViewInit(): void {
        if (!this.spatialNavigationService.enabled) {
            return;
        }

        // Document-level event delegation: Listening on document instead of container element
        // to prevent the container from becoming accessibility focusable with TalkBack.
        // Event scope is filtered to maintain intended behavior.
        fromEventElementContainsTarget<KeyboardEvent>(
            this.container.nativeElement,
            'keydown',
            this.ngZone,
            this.matDialog
        )
            .pipe(
                filter(event => this.checkDirection(event)),
                takeUntil(this.destroy$)
            )
            .subscribe(event => {
                this.onKeydown(event);
            });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private checkDirection(e: KeyboardEvent) {
        if (this.orientation === 'horizontal') {
            return e.key === 'ArrowLeft' || e.key === 'ArrowRight';
        } else if (this.orientation === 'vertical') {
            return e.key === 'ArrowUp' || e.key === 'ArrowDown';
        }
        return false;
    }

    private getOffset(dir: KeyboardEventArrowKey): number {
        let offset = 0;
        if (this.orientation === 'horizontal') {
            offset = dir === 'ArrowLeft' ? -1 : 1;
            offset *= this.isRTL ? -1 : 1;
        } else if (this.orientation === 'vertical') {
            offset = dir === 'ArrowDown' ? 1 : -1;
        }
        return offset;
    }

    private onKeydown(e: KeyboardEvent) {
        if (!this.enableFastNavigation) {
            return;
        }

        const arrowKey = e.key as KeyboardEventArrowKey;
        const offset = this.getOffset(arrowKey);
        const currentFocusedIndex = this.getCurrentFocusedIndex();
        if (currentFocusedIndex < 0) {
            return;
        }
        let nextFocusIndex = currentFocusedIndex + offset;
        const dir = getSpatialNavigationDirection(arrowKey);
        let shouldPreventDefault = false;

        while (!this.isOutofBoundary(nextFocusIndex) && !this.items.get(nextFocusIndex).focusableElement) {
            nextFocusIndex += offset;
        }

        if (!this.isOutofBoundary(nextFocusIndex) && this.items.get(nextFocusIndex).focusableElement) {
            this.items.get(nextFocusIndex).focus(dir);
            shouldPreventDefault = true;
        } else {
            shouldPreventDefault = this.spatialNavigationService.shouldContainFocus(dir, this.containFocus);
        }

        if (shouldPreventDefault) {
            e.preventDefault();
            e.stopPropagation();
        }
    }

    private isOutofBoundary(index: number): boolean {
        return index < 0 || index >= this.items.length;
    }

    /**
     * search the index of the item that contains the focus, and return -1 if not found.
     *
     * the function begins with the active element and search upward for the nvFastSpatialNavigationItem that
     * is closest to this directive. If an element has the attribute, we
     * check if it is one of the current directive's children. return the index if it is the directive's
     * children, otherwise keep searching upward until it reaches this directive's element.
     */
    private getCurrentFocusedIndex(): number {
        let currentElement = document.activeElement;
        while (currentElement && currentElement !== this.container.nativeElement) {
            if (currentElement.hasAttribute('nvFastSpatialNavigationItem')) {
                const index = this.items
                    .toArray()
                    .findIndex(
                        (item: FastSpatialNavigationItemDirective) => item?.elementRef?.nativeElement === currentElement
                    );
                if (index !== -1) {
                    return index;
                }
            }
            currentElement = currentElement.parentElement;
        }
        return -1;
    }
}
